import { useEffect, useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { auth, stats as statsApi } from "../../lib/api";
import { Calendar, ClipboardList, Crown, ImageIcon, LogOut, Map, Menu as MenuIcon, Settings as SettingsIcon, X } from "lucide-react";

const NAV = [
  { to: "/admin", label: "Overview", icon: ClipboardList, exact: true },
  { to: "/admin/reservations", label: "Reservations", icon: Crown },
  { to: "/admin/events", label: "Events", icon: Calendar },
  { to: "/admin/floor-plans", label: "Floor Plans & Tables", icon: Map },
  { to: "/admin/menu", label: "Menu", icon: MenuIcon },
  { to: "/admin/media", label: "Media", icon: ImageIcon },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
];

export default function AdminLayout() {
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!auth.getToken()) { navigate("/admin/login"); return; }
    auth.me().then(r => setMe(r.data)).catch(() => { auth.logout(); navigate("/admin/login"); });
    statsApi.get().then(setStats).catch(() => {});
  }, [navigate, location.pathname]);

  const logout = () => { auth.logout(); navigate("/admin/login"); };

  if (!me) return null;

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-black text-white">
      <header className="border-b border-white/5 sticky top-0 z-30 bg-black/90 backdrop-blur-xl">
        <div className="px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/admin" className="onyx-wordmark text-2xl">ONYX<span className="text-gold ml-2 text-xs tracking-luxe">ADMIN</span></Link>
          <div className="flex items-center gap-6">
            <span className="hidden md:block text-[11px] tracking-luxe uppercase text-white/50">{me.email}</span>
            <Link to="/" className="text-[11px] tracking-luxe uppercase text-white/60 hover:text-gold">View Site</Link>
            <button data-testid="admin-logout" onClick={logout} className="text-[11px] tracking-luxe uppercase text-white/60 hover:text-gold flex items-center gap-2"><LogOut size={12} /> Logout</button>
            <button onClick={() => setOpen(v => !v)} className="lg:hidden">{open ? <X size={20} /> : <MenuIcon size={20} />}</button>
          </div>
        </div>
      </header>

      <div className="flex">
        <aside className={`${open ? "block" : "hidden"} lg:block w-full lg:w-64 lg:min-h-[calc(100vh-4rem)] border-r border-white/5 bg-black`}>
          <nav className="p-4 space-y-1">
            {NAV.map((n) => {
              const Icon = n.icon;
              const active = n.exact ? location.pathname === n.to : location.pathname.startsWith(n.to);
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 text-[12px] tracking-luxe-tight uppercase transition ${active ? "text-gold border-l-2 border-gold bg-[var(--onyx-charcoal)]" : "text-white/60 hover:text-white border-l-2 border-transparent"}`}
                >
                  <Icon size={14} /> {n.label}
                </Link>
              );
            })}
          </nav>
          {stats && (
            <div className="m-4 p-4 border border-white/5 bg-[var(--onyx-charcoal)]">
              <p className="eyebrow mb-3">Snapshot</p>
              <div className="space-y-1 text-xs text-white/60">
                <div className="flex justify-between"><span>Pending</span><span className="text-gold">{stats.pending}</span></div>
                <div className="flex justify-between"><span>Approved</span><span>{stats.approved}</span></div>
                <div className="flex justify-between"><span>Today</span><span>{stats.today_reservations}</span></div>
                <div className="flex justify-between"><span>Events</span><span>{stats.events}</span></div>
              </div>
            </div>
          )}
        </aside>

        <main className="flex-1 p-6 md:p-10 min-h-[calc(100vh-4rem)]"><Outlet context={{ me, stats }} /></main>
      </div>
    </div>
  );
}
